import { PayerRow } from '@finpay-development/shared-platform/lib/esm/admin-configuration/models/payers';
import { VobSource } from '@finpay-development/shared-platform/lib/esm/meta-data/models/metaData';

/** Canonical cfg payor names always included in Verify Payer fan-out. */
export const STANDARD_VERIFY_PAYER_NAMES = [
  'Cigna',
  'Aetna',
  'Independence Blue Cross',
  'United Health Care',
] as const;

export type VerifyPayerCandidate = {
  payorId: number;
  payorName: string;
  pcode: string;
  isSelected: boolean;
};

/** Normalize payor names for matching (case, spaces, healthcare spelling). */
export function normalizePayorName(name: string | null | undefined): string {
  return String(name ?? '')
    .trim()
    .toLowerCase()
    .replace(/healthcare/g, 'health care')
    .replace(/\s+/g, ' ');
}

/** Availity payer code (externalCode) for a master payor. */
export function getAvailityPcodeForPayor(
  payorId: number,
  payersMap: Map<number, PayerRow> | null | undefined,
  vobSources: VobSource[] | null | undefined
): string {
  const availityVobSource = (vobSources ?? []).find(
    (source) => source.slug === 'availity'
  );
  if (!availityVobSource) {
    return '';
  }
  const payor = payersMap?.get(payorId);
  const availityPayorSource = payor?.payorSource?.find(
    (source) => source.vobSourceId === availityVobSource.vobSourceId
  );
  return availityPayorSource?.externalCode != null
    ? String(availityPayorSource.externalCode).trim()
    : '';
}

function findPayorForCanonicalName(
  canonicalName: string,
  payersMap: Map<number, PayerRow> | null | undefined,
  vobSources: VobSource[] | null | undefined
): PayerRow | null {
  if (!payersMap?.size) {
    return null;
  }
  const target = normalizePayorName(canonicalName);
  const candidates: PayerRow[] = [];

  for (const payor of payersMap.values()) {
    const pcode = getAvailityPcodeForPayor(
      Number(payor.payorId),
      payersMap,
      vobSources
    );
    if (!pcode) {
      continue;
    }
    const name = normalizePayorName(payor.payorName);
    if (!name) {
      continue;
    }
    if (
      name === target ||
      name.startsWith(target) ||
      name.includes(target)
    ) {
      candidates.push(payor);
    }
  }

  if (candidates.length === 0) {
    return null;
  }

  candidates.sort((a, b) => {
    const aName = normalizePayorName(a.payorName);
    const bName = normalizePayorName(b.payorName);
    const aExact = aName === target ? 0 : 1;
    const bExact = bName === target ? 0 : 1;
    if (aExact !== bExact) {
      return aExact - bExact;
    }
    if (aName.length !== bName.length) {
      return aName.length - bName.length;
    }
    return Number(a.payorId) - Number(b.payorId);
  });

  return candidates[0];
}

/**
 * Build unique Verify Payer candidates: standard four + selected if it has an Availity pcode.
 * Selected payor without an Availity pcode is ignored; standard payors still fan out.
 */
export function buildVerifyPayerCandidates(
  selectedPayorId: number,
  payersMap: Map<number, PayerRow> | null | undefined,
  vobSources: VobSource[] | null | undefined
): VerifyPayerCandidate[] {
  const byPayorId = new Map<number, VerifyPayerCandidate>();

  for (const canonical of STANDARD_VERIFY_PAYER_NAMES) {
    const payor = findPayorForCanonicalName(canonical, payersMap, vobSources);
    if (!payor) {
      continue;
    }
    const payorId = Number(payor.payorId);
    const pcode = getAvailityPcodeForPayor(payorId, payersMap, vobSources);
    if (!pcode) {
      continue;
    }
    byPayorId.set(payorId, {
      payorId,
      payorName: payor.payorName,
      pcode,
      isSelected: payorId === selectedPayorId,
    });
  }

  if (selectedPayorId > 0 && !byPayorId.has(selectedPayorId)) {
    const pcode = getAvailityPcodeForPayor(
      selectedPayorId,
      payersMap,
      vobSources
    );
    if (pcode) {
      const selected = payersMap?.get(selectedPayorId);
      byPayorId.set(selectedPayorId, {
        payorId: selectedPayorId,
        payorName: selected?.payorName ?? '',
        pcode,
        isSelected: true,
      });
    }
  } else if (byPayorId.has(selectedPayorId)) {
    const existing = byPayorId.get(selectedPayorId)!;
    byPayorId.set(selectedPayorId, { ...existing, isSelected: true });
  }

  return Array.from(byPayorId.values());
}
